import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Card,
  CardContent,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { SERVER_BASE_URL } from "../Config";

interface PharmacistAccount {
  id: number;
  email: string;
  name: string;
  phone: string;
  permissionLevel: string;
  temporary_password?: string;
}

interface EmailPref {
  emailType: string;
  unsubscribed: boolean;
}

const PHARMACIST_EMAIL_TYPES = [
  { key: "new_order", label: "New prescription order" },
  { key: "cancellation_request", label: "Cancellation requested" },
  { key: "provider_support", label: "Provider support request" },
  { key: "provider_request", label: "New provider account request" },
];

const PharmacistManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [pharmacists, setPharmacists] = useState<PharmacistAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create dialog state
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPermissionLevel, setNewPermissionLevel] = useState("full_access");
  const [createdPharmacist, setCreatedPharmacist] = useState<PharmacistAccount | null>(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isReactivation, setIsReactivation] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Delete dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletingPharmacist, setDeletingPharmacist] = useState<PharmacistAccount | null>(null);

  // Edit dialog state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingPharmacist, setEditingPharmacist] = useState<PharmacistAccount | null>(null);
  const [editName, setEditName] = useState("");
  const [editPermissionLevel, setEditPermissionLevel] = useState("full_access");
  const [editEmailPrefs, setEditEmailPrefs] = useState<Record<string, boolean>>({});
  const [originalEmailPrefs, setOriginalEmailPrefs] = useState<Record<string, boolean>>({});

  // Tabs and deleted state
  const [activeTab, setActiveTab] = useState(0);
  const [deletedPharmacists, setDeletedPharmacists] = useState<PharmacistAccount[]>([]);
  const [reactivatingPharmacist, setReactivatingPharmacist] = useState<PharmacistAccount | null>(null);
  const [openReactivateDialog, setOpenReactivateDialog] = useState(false);

  const loadPharmacists = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/pharmacists`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setPharmacists(data.pharmacists);
      } else {
        setError(data.message || "Failed to load pharmacists");
      }
    } catch (err) {
      setError(`Failed to load pharmacists: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedPharmacists = async () => {
    try {
      const token = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/pharmacists/deleted`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setDeletedPharmacists(data.pharmacists);
      }
    } catch (err) {
      setError(`Failed to load deleted pharmacists: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  useEffect(() => {
    loadPharmacists();
    loadDeletedPharmacists();
  }, []);

  const handleCreatePharmacist = async () => {
    if (!newEmail || !newName) {
      setError("Email and name are required");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/pharmacists`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: newEmail,
          name: newName,
          permission_level: newPermissionLevel,
        }),
      });

      const data = await response.json();
      if (data.success && data.pharmacist) {
        setIsPasswordReset(false);
        setIsReactivation(false);
        setEmailSent(!!data.email_sent);
        setCreatedPharmacist(data.pharmacist);
        setSuccess("Pharmacist created successfully!");
        loadPharmacists();
        setNewEmail("");
        setNewName("");
        setNewPermissionLevel("full_access");
      } else {
        setError(data.message || "Failed to create pharmacist");
      }
    } catch (err) {
      setError(`Failed to create pharmacist: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (pharmacistId: number, pharmacistName: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Reset password for ${pharmacistName}? This will generate a new temporary password.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/pharmacists/${pharmacistId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        const pharmacistWithPassword = pharmacists.find(p => p.id === pharmacistId);
        if (pharmacistWithPassword) {
          setIsPasswordReset(true);
          setIsReactivation(false);
          setEmailSent(!!data.email_sent);
          setCreatedPharmacist({
            ...pharmacistWithPassword,
            temporary_password: data.temporary_password
          });
          setOpenCreateDialog(true);
        }
        setSuccess("Password reset successfully");
      } else {
        setError(data.message || "Failed to reset password");
      }
    } catch (err) {
      setError(`Failed to reset password: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = (password: string) => {
    navigator.clipboard.writeText(password);
    setSuccess("Password copied to clipboard!");
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setCreatedPharmacist(null);
    setIsPasswordReset(false);
    setIsReactivation(false);
    setEmailSent(false);
  };

  const handleOpenEditDialog = async (pharmacist: PharmacistAccount) => {
    setEditingPharmacist(pharmacist);
    setEditName(pharmacist.name);
    setEditPermissionLevel(pharmacist.permissionLevel || "full_access");
    setOpenEditDialog(true);
    // Fetch email preferences for this pharmacist
    try {
      const token = localStorage.getItem("pharmacistToken");
      const res = await fetch(`${SERVER_BASE_URL}/admin/email-preferences/pharmacist/${pharmacist.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const map: Record<string, boolean> = {};
        for (const p of data.preferences) {
          map[p.emailType] = p.unsubscribed;
        }
        setEditEmailPrefs(map);
        setOriginalEmailPrefs(map);
      }
    } catch (err) {
      console.error("Failed to load email preferences:", err);
    }
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingPharmacist(null);
    setEditName("");
    setEditPermissionLevel("full_access");
    setEditEmailPrefs({});
    setOriginalEmailPrefs({});
  };

  const handleSavePharmacist = async () => {
    if (!editingPharmacist || !editName) {
      setError("Name is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/pharmacists/${editingPharmacist.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editName,
          permission_level: editPermissionLevel,
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Save email preference changes
        const token2 = localStorage.getItem("pharmacistToken");
        for (const et of PHARMACIST_EMAIL_TYPES) {
          if (editEmailPrefs[et.key] !== originalEmailPrefs[et.key]) {
            await fetch(`${SERVER_BASE_URL}/admin/email-preferences/pharmacist/${editingPharmacist.id}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token2}`,
              },
              body: JSON.stringify({ emailType: et.key, unsubscribed: editEmailPrefs[et.key] ?? true }),
            });
          }
        }
        setSuccess("Pharmacist updated successfully!");
        loadPharmacists();
        handleCloseEditDialog();
      } else {
        setError(data.message || "Failed to update pharmacist");
      }
    } catch (err) {
      setError(`Failed to update pharmacist: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEmailPref = (emailType: string, currentlyOn: boolean) => {
    const newUnsubscribed = currentlyOn;
    setEditEmailPrefs((prev) => ({ ...prev, [emailType]: newUnsubscribed }));
  };

  const handleDeletePharmacist = async () => {
    if (!deletingPharmacist) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/pharmacists/${deletingPharmacist.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`${deletingPharmacist.name} has been deactivated.`);
        setOpenDeleteDialog(false);
        setDeletingPharmacist(null);
        loadPharmacists();
        loadDeletedPharmacists();
      } else {
        setError(data.message || "Failed to deactivate pharmacist");
      }
    } catch (err) {
      setError(`Failed to deactivate pharmacist: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivatePharmacist = async () => {
    if (!reactivatingPharmacist) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/pharmacists/deleted/${reactivatingPharmacist.id}/reactivate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setOpenReactivateDialog(false);
        setReactivatingPharmacist(null);
        setIsPasswordReset(true);
        setIsReactivation(true);
        setEmailSent(!!data.email_sent);
        setCreatedPharmacist({
          ...data.pharmacist,
          temporary_password: data.temporary_password
        });
        setOpenCreateDialog(true);
        setSuccess("Pharmacist reactivated successfully!");
        loadPharmacists();
        loadDeletedPharmacists();
      } else {
        setError(data.message || "Failed to reactivate pharmacist");
      }
    } catch (err) {
      setError(`Failed to reactivate pharmacist: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const renderPermissionChip = (level: string) => {
    return level === "full_access" ? (
      <Chip label="Full Access" size="small" color="primary" sx={{ width: 95 }} />
    ) : (
      <Chip label="Restricted" size="small" variant="outlined" sx={{ width: 95 }} />
    );
  };

  const PHARMACY_LOGIN_URL = "https://providers.thechemistshoppharmacy.com/pharmacy-login";

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 0, sm: 3 } }} elevation={isMobile ? 0 : 1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant={isMobile ? "h6" : "h5"}>Admin Accounts</Typography>
          {activeTab === 0 && (
            <Button
              variant="contained"
              startIcon={isMobile ? undefined : <PersonAddIcon />}
              onClick={() => setOpenCreateDialog(true)}
              disabled={loading}
              size={isMobile ? "small" : "medium"}
              sx={{ whiteSpace: "nowrap", ml: 1, fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
            >
              {isMobile ? "Create" : "Create Account"}
            </Button>
          )}
        </Box>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="Active" />
          <Tab label="Deactivated" />
        </Tabs>

        {activeTab === 1 ? (
          isMobile ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {deletedPharmacists.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                  No deactivated accounts
                </Typography>
              )}
              {deletedPharmacists.map((pharmacist) => (
                <Card key={pharmacist.id} variant="outlined">
                  <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                          {pharmacist.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", wordBreak: "break-all" }}>
                          {pharmacist.email}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RestoreIcon />}
                        onClick={() => { setReactivatingPharmacist(pharmacist); setOpenReactivateDialog(true); }}
                        disabled={loading}
                        sx={{ ml: 1, whiteSpace: "nowrap" }}
                      >
                        Reactivate
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Permission</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedPharmacists.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">
                          No deactivated accounts
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {deletedPharmacists.map((pharmacist) => (
                    <TableRow key={pharmacist.id}>
                      <TableCell>{pharmacist.name}</TableCell>
                      <TableCell>{pharmacist.email}</TableCell>
                      <TableCell>{renderPermissionChip(pharmacist.permissionLevel)}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          startIcon={<RestoreIcon />}
                          onClick={() => { setReactivatingPharmacist(pharmacist); setOpenReactivateDialog(true); }}
                          disabled={loading}
                        >
                          Reactivate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {pharmacists.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                No accounts found
              </Typography>
            )}
            {pharmacists.map((pharmacist) => (
              <Card key={pharmacist.id} variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        {pharmacist.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", wordBreak: "break-all" }}>
                        {pharmacist.email}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {renderPermissionChip(pharmacist.permissionLevel)}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", ml: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(pharmacist)}
                        disabled={loading}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleResetPassword(pharmacist.id, pharmacist.name)}
                        disabled={loading}
                      >
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => { setDeletingPharmacist(pharmacist); setOpenDeleteDialog(true); }}
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Permission</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pharmacists.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary">
                        No accounts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {pharmacists.map((pharmacist) => (
                  <TableRow key={pharmacist.id}>
                    <TableCell>{pharmacist.name}</TableCell>
                    <TableCell>{pharmacist.email}</TableCell>
                    <TableCell>{renderPermissionChip(pharmacist.permissionLevel)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(pharmacist)}
                        disabled={loading}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => handleResetPassword(pharmacist.id, pharmacist.name)}
                        disabled={loading}
                        title="Reset Password"
                      >
                        <LockResetIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => { setDeletingPharmacist(pharmacist); setOpenDeleteDialog(true); }}
                        disabled={loading}
                        title="Deactivate"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create / Password Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth sx={{}}>
        <DialogTitle>
          {createdPharmacist ? "Account Password" : "Create New Admin Account"}
        </DialogTitle>
        <DialogContent>
          {createdPharmacist ? (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Name:</strong> {createdPharmacist.name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {createdPharmacist.email}
              </Typography>
              {emailSent ? (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
                  {isReactivation ? "Reactivation" : isPasswordReset ? "Password reset" : "Welcome"} email sent to {createdPharmacist.email}
                </Alert>
              ) : (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Password ready!
                  </Alert>
                  <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Temporary Password:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={createdPharmacist.temporary_password}
                        sx={{ fontFamily: "monospace", fontSize: "1rem" }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleCopyPassword(createdPharmacist.temporary_password!)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
                      Make sure to copy this password - it won't be shown again!
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<EmailIcon />}
                    sx={{ mt: 2 }}
                    fullWidth
                    onClick={() => {
                      const subject = isReactivation
                        ? "Your Chemist Shop Pharmacy Account Has Been Reactivated"
                        : isPasswordReset ? "Your Chemist Shop Pharmacy Password Has Been Reset" : "Your Chemist Shop Pharmacy Account";
                      const body = isReactivation
                        ? `Hi ${createdPharmacist.name},\n\nYour pharmacy account on The Chemist Shop has been reactivated.\n\nEmail: ${createdPharmacist.email}\nTemporary Password: ${createdPharmacist.temporary_password}\n\nPlease click the link below to log in and change your password:\n${PHARMACY_LOGIN_URL}\n\nThank you,\nThe Chemist Shop`
                        : isPasswordReset
                        ? `Hi ${createdPharmacist.name},\n\nYour pharmacy password for The Chemist Shop has been reset.\n\nEmail: ${createdPharmacist.email}\nTemporary Password: ${createdPharmacist.temporary_password}\n\nPlease click the link below to log in and change your password:\n${PHARMACY_LOGIN_URL}\n\nThank you,\nThe Chemist Shop`
                        : `Hi ${createdPharmacist.name},\n\nYour pharmacy account on The Chemist Shop has been created.\n\nEmail: ${createdPharmacist.email}\nTemporary Password: ${createdPharmacist.temporary_password}\n\nPlease click the link below to log in and change your password:\n${PHARMACY_LOGIN_URL}\n\nThank you,\nThe Chemist Shop`;
                      window.location.href = `mailto:${encodeURIComponent(createdPharmacist.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    }}
                  >
                    {isReactivation ? "Send Reactivation Email" : isPasswordReset ? "Send Password Reset Email" : "Send Welcome Email"}
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
              />
              <TextField
                margin="dense"
                label="Email"
                type="email"
                fullWidth
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Permission Level</InputLabel>
                <Select
                  value={newPermissionLevel}
                  label="Permission Level"
                  onChange={(e) => setNewPermissionLevel(e.target.value)}
                >
                  <MenuItem value="full_access">Full Access</MenuItem>
                  <MenuItem value="restricted">Restricted</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                A random 10-character password will be generated automatically.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>
            {createdPharmacist ? "Close" : "Cancel"}
          </Button>
          {!createdPharmacist && (
            <Button
              onClick={handleCreatePharmacist}
              variant="contained"
              disabled={loading || !newEmail || !newName}
            >
              Create Account
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => { setOpenDeleteDialog(false); setDeletingPharmacist(null); }} maxWidth="xs" fullWidth sx={{}}>
        <DialogTitle>Deactivate Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate <strong>{deletingPharmacist?.name}</strong>? They will no longer be able to log in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDeleteDialog(false); setDeletingPharmacist(null); }}>Cancel</Button>
          <Button onClick={handleDeletePharmacist} variant="contained" color="error" disabled={loading}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate Confirmation Dialog */}
      <Dialog open={openReactivateDialog} onClose={() => { setOpenReactivateDialog(false); setReactivatingPharmacist(null); }} maxWidth="xs" fullWidth sx={{}}>
        <DialogTitle>Reactivate Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reactivate <strong>{reactivatingPharmacist?.name}</strong>? This will reset their password and restore access.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenReactivateDialog(false); setReactivatingPharmacist(null); }}>Cancel</Button>
          <Button onClick={handleReactivatePharmacist} variant="contained" color="primary" disabled={loading}>
            Reactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth sx={{}}>
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            {editingPharmacist && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Email:</strong> {editingPharmacist.email}
              </Typography>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              type="text"
              fullWidth
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              required
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Permission Level</InputLabel>
              <Select
                value={editPermissionLevel}
                label="Permission Level"
                onChange={(e) => setEditPermissionLevel(e.target.value)}
              >
                <MenuItem value="full_access">Full Access</MenuItem>
                <MenuItem value="restricted">Restricted</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Email Notifications
            </Typography>
            {PHARMACIST_EMAIL_TYPES.map((et) => {
              // Pharmacists are off by default — only on if explicitly set to unsubscribed=false
              const isOn = editEmailPrefs[et.key] === false;
              return (
                <FormControlLabel
                  key={et.key}
                  control={
                    <Switch
                      checked={isOn}
                      onChange={() => handleToggleEmailPref(et.key, isOn)}
                    />
                  }
                  label={et.label}
                  sx={{ display: "block" }}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleSavePharmacist}
            variant="contained"
            disabled={loading || !editName}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PharmacistManagement;
